import type React from "react";
import { createContext, useContext, useEffect, useMemo, useState } from "react";
import type { LoginResponse } from "@qbanq/shared";
import { api, getToken, setToken } from "../api/client";

interface AuthState {
  user: LoginResponse["user"] | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
}

const AuthContext = createContext<AuthState | null>(null);
const USER_KEY = "qbanq.user";

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<LoginResponse["user"] | null>(() => {
    const raw = localStorage.getItem(USER_KEY);
    return raw ? (JSON.parse(raw) as LoginResponse["user"]) : null;
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    // If there is no token, ensure user is cleared.
    if (!getToken()) {
      setUser(null);
      localStorage.removeItem(USER_KEY);
    }
  }, []);

  const value = useMemo<AuthState>(
    () => ({
      user,
      loading,
      async login(email, password) {
        setLoading(true);
        try {
          const res = await api.post<LoginResponse>("/api/auth/login", { email, password });
          setToken(res.token);
          localStorage.setItem(USER_KEY, JSON.stringify(res.user));
          setUser(res.user);
        } finally {
          setLoading(false);
        }
      },
      logout() {
        setToken(null);
        localStorage.removeItem(USER_KEY);
        setUser(null);
      },
    }),
    [user, loading],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
